import { MDBAlert, MDBBtn, MDBBtnGroup, MDBCard, MDBCardBody, MDBCardTitle, MDBCol, MDBIcon, MDBModal, MDBModalBody, MDBModalContent, MDBModalDialog, MDBModalHeader, MDBModalTitle, MDBRow, MDBTable, MDBTableBody, MDBTableHead, MDBSpinner } from "mdb-react-ui-kit";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { isoToGermanDate } from "../../../utils/dateTools";
import moment from 'moment-timezone';
import { useIndustry } from "../../../utils/industryContextHook";
import { UserContext, DevContext } from "../../../App";
import LectureRegistration from "../../../components-user/event-management/lecture-registration/Index";
import { captureError } from "../../../utils/sentryHelpers";
// Enhanced imports using new hooks and validation
import { useLecturesManager } from "../../../components-user/event-management/lecture-registration/hooks/useLecturesManager";
import { useEventsManager } from "../../../components-user/event-management/lecture-registration/hooks/useEventsManager";
import { lounges26ActionsSchema } from "../../../components-user/event-management/lecture-registration/validation/lectures";
import lounges26ActionConfig from "./actionEventConfig";

export default function AllActions() {
    const { t, i18n } = useTranslation();
    const { currentIndustry } = useIndustry();
    const { user } = useContext(UserContext);
    const { isDevMode } = useContext(DevContext);

    const companyid = window.location.pathname.includes("mycompany")
    ? window.location.pathname.split("/")[2]
    : window.location.pathname.split("/")[3];

    // Enhanced hooks for data management with Actions configuration
    const {
        lectures,
        validationErrors,
        isLoading: isLoadingLectures,
        isDeletingLecture,
        error: lecturesError,
        deleteLecture: deleteLectureFromHook,
        addLecture,
        updateLecture,
        getLectureValidationErrors,
        isLectureValid,
        canEditLecture,
        canDeleteLecture,
        refreshLectures
    } = useLecturesManager(companyid, currentIndustry?.id, user, {
        entityType: 'action',
        validationSchema: lounges26ActionsSchema
    });

    const {
        isLoading: isLoadingEvents,
        error: eventsError,
        isRegistrationOpen,
        isRegistrationUpcoming,
        getEventsByRegistrationStatus
    } = useEventsManager(companyid, currentIndustry?.id);

    // Modal states
    const [deleteLectureModal, setDeleteLectureModal] = useState(false);
    const toggleDeleteLectureModal = () => setDeleteLectureModal(!deleteLectureModal);

    const [selectedLectureRemove, setSelectedLectureRemove] = useState(null);

    // Modal for Action Registration
    const [showActionRegistration, setShowActionRegistration] = useState(false);
    const [selectedActionForEdit, setSelectedActionForEdit] = useState(null);
    const [selectedEventForAction, setSelectedEventForAction] = useState(null);

    const openActionRegistration = (event, actionId = null) => {
        setSelectedEventForAction(event);
        setSelectedActionForEdit(actionId);
        setShowActionRegistration(true);
    };

    const closeActionRegistration = () => {
        setShowActionRegistration(false);
        setSelectedActionForEdit(null);
        setSelectedEventForAction(null);
        // Refresh is now automatic via hook
    };

    const handleSaveLecture = (lectureData) => {
        console.log('Lecture saved:', lectureData);

        // Enrich lecture data with event relation if missing
        const enrichedLectureData = {
            ...lectureData,
            event: lectureData.event || selectedEventForAction
        };

        // Update local state based on whether it's new or edited
        if (selectedActionForEdit && selectedActionForEdit !== "new") {
            updateLecture(enrichedLectureData);
        } else {
            addLecture(enrichedLectureData);
        }

        // Close modal
        closeActionRegistration();
    };

    const deleteLecture = async (id) => {
        try {
            await deleteLectureFromHook(id);
            setSelectedLectureRemove(null);
            toggleDeleteLectureModal();
        } catch (error) {
            console.log("error on deleting lecture", error);
            captureError(error, {
                feature: 'event-management',
                operation: 'delete-action',
                consoleMessage: 'error on deleting lecture',
                context: {
                    lectureId: id,
                    companyId: companyid
                }
            });
        }
    };

    // DevMode-aware permission checks
    const canEditLectureDevMode = (lecture) => {
        if (isDevMode) {
            return true;
        }
        return canEditLecture(lecture);
    };

    const canDeleteLectureDevMode = (lecture) => {
        if (isDevMode) {
            return true;
        }
        return canDeleteLecture(lecture);
    };

    // Helper function to check if an event is old (endDate > 14 days ago)
    const isOldEvent = (event) => {
        if (!event?.endDate) return false;
        const endDate = new Date(event.endDate);
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
        return endDate < fourteenDaysAgo;
    };

    // Separate actions into current and old event actions
    const currentEventActions = lectures.filter(lecture => !isOldEvent(lecture.event));
    const oldEventActions = lectures.filter(lecture => isOldEvent(lecture.event));

    // Show loading state
    if (isLoadingLectures || isLoadingEvents) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                <MDBSpinner style={{ width: '3rem', height: '3rem' }} className="me-3">
                    <span className="visually-hidden">{t("Loading...")}</span>
                </MDBSpinner>
                <span>{t('Loading promotions and events...')}</span>
            </div>
        );
    }

    // Show error state
    if (lecturesError || eventsError) {
        return (
            <MDBAlert color="danger" className="mb-4">
                <h4 className="alert-heading">{t('Error Loading Data')}</h4>
                <p>{lecturesError || eventsError}</p>
                <hr />
                <p className="mb-0">
                    <MDBBtn color="danger" outline onClick={() => {
                        refreshLectures();
                    }}>
                        {t('Try Again')}
                    </MDBBtn>
                </p>
            </MDBAlert>
        );
    }

  return (
<>

{/* Enhanced Events Section for Actions */}
<MDBRow className="mb-3 mb-lg-5">
    {getEventsByRegistrationStatus('active').map((event) => (
        <MDBCol size="12" lg="6" key={event.id}>
            <MDBCard className="mx-auto text-center h-100" style={isRegistrationUpcoming(event) ? {backgroundColor: "#f8f9fa"} : {}}>
                <MDBCardBody className="d-flex flex-column">
                    <MDBCardTitle>
                        <h4>{event.name.find(n => n.language === i18n.language)?.text || event.name[0].text}</h4>
                    </MDBCardTitle>

                    {isRegistrationOpen(event) && (
                        <h6 className="text-success mb-3">
                            <MDBIcon fas icon="check-circle" className="me-2" />
                            {t("Submission Open for Promotions")}
                        </h6>
                    )}

                    {isRegistrationUpcoming(event) && (
                        <div className="mb-3">
                            <h6 className="text-muted">
                                <MDBIcon fas icon="clock" className="me-2" />
                                {t("Submission opens on")}
                            </h6>
                            <p className="fw-bold">{isoToGermanDate(event.lectureRegistrationStartDate)}</p>
                        </div>
                    )}

                    <div className="mb-3 small text-muted">
                        {event.lectureRegistrationEndDate && (
                            <div><strong>{t("Submission Deadline")}:</strong> {isoToGermanDate(event.lectureRegistrationEndDate)}</div>
                        )}
                        <div><strong>{t("Event Date")}:</strong> {isoToGermanDate(event.startDate)} - {isoToGermanDate(event.endDate)}</div>
                    </div>

                    <div className="mt-auto">
                        {/* Im DevMode sind Register-Buttons immer verfügbar */}
                        {isDevMode ? (
                            <div>
                                <MDBBtn color="primary" onClick={() => openActionRegistration(event, "new")}>
                                    <MDBIcon fas icon="plus" className="me-2" />
                                    {t("Register Promotion")}
                                </MDBBtn>
                                <small className="text-info d-block text-center mt-2">
                                    <MDBIcon fas icon="code" className="me-1" />
                                    DevMode: Always enabled
                                </small>
                            </div>
                        ) : (
                            <>
                                {isRegistrationOpen(event) ? (
                                    <div>
                                        <MDBBtn color="primary" onClick={() => openActionRegistration(event, "new")}>
                                            <MDBIcon fas icon="plus" className="me-2" />
                                            {t("Register Promotion")}
                                        </MDBBtn>
                                    </div>
                                ) : (
                                    <MDBBtn color="secondary" disabled>
                                        {isRegistrationUpcoming(event)
                                            ? t("Submission Not Yet Open")
                                            : t("Submission Closed")
                                        }
                                    </MDBBtn>
                                )}
                            </>
                        )}
                    </div>
                </MDBCardBody>
            </MDBCard>
        </MDBCol>
    ))}

    {getEventsByRegistrationStatus('active').length === 0 && (
        <MDBCol size="12">
            <MDBAlert color="info" className="text-center">
                <h5>{t("No Events Available")}</h5>
                <p>{t("There are currently no events available for promotion registration. Please check back later or contact support if you believe this is an error.")}</p>
            </MDBAlert>
        </MDBCol>
    )}
</MDBRow>

{/* Dev Mode: Overall Validation Summary */}
{isDevMode && (
    <MDBCard className="mb-4 border-warning">
        <MDBCardBody className="bg-light">
            <MDBCardTitle className="text-warning">
                <MDBIcon fas icon="code" className="me-2" />
                Dev Mode: Validation Summary
            </MDBCardTitle>
            <div className="row">
                <div className="col-md-6">
                    <h6>Overall Validation Errors:</h6>
                    <pre className="small" style={{fontSize: '11px', maxHeight: '200px', overflow: 'auto'}}>
                        {JSON.stringify(validationErrors, null, 2)}
                    </pre>
                </div>
                <div className="col-md-6">
                    <h6>Promotion Stats:</h6>
                    <ul className="small">
                        <li>Total Promotions: {lectures.length}</li>
                        <li>Valid Promotions: {lectures.filter(l => isLectureValid(l.id)).length}</li>
                        <li>Invalid Promotions: {lectures.filter(l => !isLectureValid(l.id)).length}</li>
                    </ul>
                </div>
            </div>
        </MDBCardBody>
    </MDBCard>
)}

<MDBCard>
    <MDBCardBody>
        <MDBCardTitle>
            <h2>{t("Promotions")}</h2>
        </MDBCardTitle>
        <MDBTable>
            <MDBTableHead>
                <tr>
                <th>{t("Event")}</th>
                <th>{t("Title")}</th>
                <th>{t("Type")}</th>
                    <th>{t("Speakers")}</th>
                    <th>{t("Date and Time")}</th>
                    <th>{t("Location")}</th>
                    <th>{t("Status")}</th>
                    <th>{t("Actions")}</th>

                </tr>
            </MDBTableHead>
            <MDBTableBody>
                {lectures.length > 0 ? (
                    <>
                    {/* Current event actions */}
                    {currentEventActions.map((lecture) => {
                        const lectureErrors = getLectureValidationErrors(lecture.id);
                        const isValid = isLectureValid(lecture.id);

                        return (
                            <tr key={lecture.id}>
                            <td>
                                <small className="text-muted">
                                    {lecture.event?.name?.find(n => n.language === i18n.language)?.text || lecture.event?.name?.[0]?.text}
                                </small>
                            </td>
                            <td>
                                <strong>{lecture.title?.find(t => t.language === i18n.language)?.text || lecture.title?.[0]?.text}</strong>
                            </td>
                            <td>
                                <span className="badge bg-secondary">
                                    {t(lecture.type)}
                                </span>
                            </td>
                            <td>
                                <div className="d-flex align-items-center">
                                    {lecture.speakers?.items?.slice(0, 2).map((speaker, index) => (
                                        <div key={index} className="me-1">
                                            {speaker?.employee?.image?.fileName ? (
                                                <img
                                                    src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speaker.employee.image.fileName}`}
                                                    className="rounded-circle border"
                                                    style={{
                                                        height: "30px",
                                                        width: "30px",
                                                        objectFit: "cover",
                                                    }}
                                                    alt={speaker.employee?.image.alt}
                                                    title={`${speaker.employee?.user?.firstName} ${speaker.employee?.user?.lastName}`}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded-circle border bg-light d-flex align-items-center justify-content-center"
                                                    style={{
                                                        height: "30px",
                                                        width: "30px",
                                                    }}
                                                >
                                                    <MDBIcon fas icon="user" size="sm" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {lecture.speakers?.items?.length > 2 && (
                                        <small className="text-muted">+{lecture.speakers.items.length - 2}</small>
                                    )}
                                </div>
                            </td>
                            <td>
                              {lecture.dateTimes && lecture.dateTimes.length > 0 ?
                                lecture.dateTimes.map((dateTime, idx) => {
                                  // Konvertiere das ISO-Datum in die deutsche Zeitzone
                                  const dateInGermany = moment(dateTime).tz("Europe/Berlin");
                                  return (
                                    <div key={idx}>
                                      {isoToGermanDate(dateInGermany.format())} - {dateInGermany.format('HH:mm')} Uhr
                                    </div>
                                  );
                                })
                                : <span className="text-muted">{t("not yet determined")}</span>}
                            </td>
                            <td>{lecture.companyBooth || <span className="text-muted">{t("not yet determined")}</span>}</td>
                            <td>
                                {isValid ? (
                                    <div className="text-success">
                                        <MDBIcon fas icon="check-circle" className="me-1" />
                                        <small>{t("Complete")}</small>
                                    </div>
                                ) : (
                                    <div className="text-warning">
                                        <MDBIcon fas icon="exclamation-circle" className="me-1" />
                                        <small>{t("Incomplete")}</small>
                                        <div className="mt-1">
                                            {lectureErrors.slice(0, 3).map((error, index) => (
                                                <div key={index} className="small text-danger mb-1">
                                                    <MDBIcon fas icon="times-circle" size="sm" className="me-1" />
                                                    <strong>{t(error.field)}:</strong> {t(error.message)}
                                                </div>
                                            ))}
                                            {lectureErrors.length > 3 && (
                                                <small className="text-muted">+{lectureErrors.length - 3} {t("more errors")}</small>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </td>
                            <td>
                            <MDBBtnGroup shadow="0">
                                {canEditLectureDevMode(lecture) && (
                                    <MDBBtn
                                        color='link'
                                        className="px-2"
                                        onClick={() => openActionRegistration(lecture.event, lecture.id)}
                                    >
                                        {t("Edit")}
                                        {isDevMode && !canEditLecture(lecture) && (
                                            <MDBIcon fas icon="tools" className="ms-1 text-warning" title="DevMode: Normally disabled" />
                                        )}
                                    </MDBBtn>
                                )}
                                {canDeleteLectureDevMode(lecture) && (
                                    <MDBBtn
                                        color='link'
                                        style={{color: "red"}}
                                        className="px-2"
                                        onClick={() => {
                                            setSelectedLectureRemove(lecture.id);
                                            toggleDeleteLectureModal();
                                        }}
                                    >
                                        {t("Delete")}
                                        {isDevMode && !canDeleteLecture(lecture) && (
                                            <MDBIcon fas icon="tools" className="ms-1 text-warning" title="DevMode: Normally disabled" />
                                        )}
                                    </MDBBtn>
                                )}
                            </MDBBtnGroup>
                            </td>
                        </tr>
                        );
                    })}

                    {/* Separator for old event actions */}
                    {oldEventActions.length > 0 && (
                        <tr className="bg-light">
                            <td colSpan="8" className="py-3">
                                <div className="d-flex align-items-center">
                                    <hr className="flex-grow-1 me-3" />
                                    <span className="text-muted fw-bold">
                                        <MDBIcon fas icon="history" className="me-2" />
                                        {t("Old event promotions")}
                                    </span>
                                    <hr className="flex-grow-1 ms-3" />
                                </div>
                            </td>
                        </tr>
                    )}

                    {/* Old event actions */}
                    {oldEventActions.map((lecture) => {
                        return (
                            <tr key={lecture.id} className="table-secondary">
                            <td>
                                <small className="text-muted">
                                    {lecture.event?.name?.find(n => n.language === i18n.language)?.text || lecture.event?.name?.[0]?.text}
                                </small>
                            </td>
                            <td>
                                <strong>{lecture.title?.find(t => t.language === i18n.language)?.text || lecture.title?.[0]?.text}</strong>
                            </td>
                            <td>
                                <span className="badge bg-secondary">
                                    {t(lecture.type)}
                                </span>
                            </td>
                            <td>
                                <div className="d-flex align-items-center">
                                    {lecture.speakers?.items?.slice(0, 2).map((speaker, index) => (
                                        <div key={index} className="me-1">
                                            {speaker?.employee?.image?.fileName ? (
                                                <img
                                                    src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/userImages/${speaker.employee.image.fileName}`}
                                                    className="rounded-circle border"
                                                    style={{
                                                        height: "30px",
                                                        width: "30px",
                                                        objectFit: "cover",
                                                    }}
                                                    alt={speaker.employee?.image.alt}
                                                    title={`${speaker.employee?.user?.firstName} ${speaker.employee?.user?.lastName}`}
                                                />
                                            ) : (
                                                <div
                                                    className="rounded-circle border bg-light d-flex align-items-center justify-content-center"
                                                    style={{
                                                        height: "30px",
                                                        width: "30px",
                                                    }}
                                                >
                                                    <MDBIcon fas icon="user" size="sm" />
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {lecture.speakers?.items?.length > 2 && (
                                        <small className="text-muted">+{lecture.speakers.items.length - 2}</small>
                                    )}
                                </div>
                            </td>
                            <td>
                              {lecture.dateTimes && lecture.dateTimes.length > 0 ?
                                lecture.dateTimes.map((dateTime, idx) => {
                                  const dateInGermany = moment(dateTime).tz("Europe/Berlin");
                                  return (
                                    <div key={idx}>
                                      {isoToGermanDate(dateInGermany.format())} - {dateInGermany.format('HH:mm')} Uhr
                                    </div>
                                  );
                                })
                                : <span className="text-muted">{t("not yet determined")}</span>}
                            </td>
                            <td>{lecture.companyBooth || <span className="text-muted">{t("not yet determined")}</span>}</td>
                            <td>
                                {/* Status display for old actions */}
                                {lecture.status && lecture.status !== "created" ? (
                                    <div className={`${
                                        lecture.status === "checked" ? "text-success" :
                                        lecture.status === "not_approved" ? "text-danger" :
                                        lecture.status === "in_planning" ? "text-warning" :
                                        lecture.status === "committed" ? "text-primary" :
                                        "text-muted"
                                    }`}>
                                        <small>
                                            <MDBIcon fas icon={
                                                lecture.status === "checked" ? "check-circle" :
                                                lecture.status === "not_approved" ? "times-circle" :
                                                lecture.status === "in_planning" ? "clock" :
                                                lecture.status === "committed" ? "calendar-check" :
                                                "info-circle"
                                            } className="me-1" />
                                            {lecture.status === "checked" ? t("Approved") :
                                             lecture.status === "not_approved" ? t("Not Approved") :
                                             lecture.status === "in_planning" ? t("In Planning") :
                                             lecture.status === "committed" ? t("Committed") :
                                             t(lecture.status)}
                                        </small>
                                    </div>
                                ) : null}
                            </td>
                            <td>
                                {/* Old actions: only view, no edit/delete */}
                                <small className="text-muted">
                                    <MDBIcon fas icon="archive" className="me-1" />
                                    {t("Archived")}
                                </small>
                            </td>
                        </tr>
                        );
                    })}
                    </>
                ) : (
                    <tr>
                        <td colSpan="8" className="text-center py-5">
                            <MDBIcon fas icon="bullhorn" size="3x" className="text-muted mb-3" />
                            <h5 className="text-muted">{t("No promotions found")}</h5>
                            <p className="text-muted">{t("Register your first promotion for an upcoming event.")}</p>
                        </td>
                    </tr>
                )}
            </MDBTableBody>
        </MDBTable>
    </MDBCardBody>
</MDBCard>

<MDBModal open={deleteLectureModal} onClose={() => setDeleteLectureModal(false)}>
    <MDBModalDialog centered size="sm">
        <MDBModalContent>
        <MDBModalHeader>
            <MDBModalTitle>{t("Remove promotion")}</MDBModalTitle>
            <MDBBtn className='btn-close' color='none' onClick={toggleDeleteLectureModal}></MDBBtn>
        </MDBModalHeader>
        <MDBModalBody className="text-center">
            <MDBIcon fas icon="exclamation-triangle" size="3x" className="text-warning mb-3" />
            <p>{t("Are you sure you want to remove this promotion?")}</p>
            <p className="fw-bold">
                {selectedLectureRemove && (
                    lectures.find(l => l.id === selectedLectureRemove)?.title.find(t => t.language === i18n.language)?.text ||
                    lectures.find(l => l.id === selectedLectureRemove)?.title[0]?.text
                )}
            </p>
            <div className="d-flex gap-2 justify-content-center">
                <MDBBtn color='secondary' onClick={toggleDeleteLectureModal}>
                    {t("Cancel")}
                </MDBBtn>
                <MDBBtn
                    color='danger'
                    onClick={() => deleteLecture(selectedLectureRemove)}
                    disabled={isDeletingLecture}
                >
                    {isDeletingLecture ? (
                        <><MDBSpinner size='sm' className='me-2' />{t("Deleting...")}</>
                    ) : (
                        <><MDBIcon fas icon="trash" className="me-1" />{t("Remove")}</>
                    )}
                </MDBBtn>
            </div>
        </MDBModalBody>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>

{/* Modal for Action Registration */}
<MDBModal open={showActionRegistration} onClose={closeActionRegistration} staticBackdrop>
    <MDBModalDialog size="fullscreen">
        <MDBModalContent>
            <MDBModalBody className="p-0">
                    <LectureRegistration
                        companyId={companyid}
                        eventId={selectedEventForAction?.id}
                        lectureId={selectedActionForEdit}
                        eventConfig={lounges26ActionConfig}
                        onSaveLecture={handleSaveLecture}
                        onCloseRequest={closeActionRegistration}
                        isModal={true}
                    />
            </MDBModalBody>
        </MDBModalContent>
    </MDBModalDialog>
</MDBModal>
</>
  )
}
